import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  getAllStudents,
  getAllTeachers,
  getAllInstitutes,
  approveTeacher,
  approveInstitute,
  updateUserStatus,
  deleteUser
} from './adminService';
import { getMultipleTeacherRatings } from './bookingService';
import RatingBreakdown from './components/RatingBreakdown';
import RecentReviews from './components/RecentReviews';
import { supabase } from './supabaseClient';
import Step10Review from './components/wizard/Step10Review';
import { wizardTranslations } from './TeacherSignupWizard';
import {
  Users,
  GraduationCap,
  Building2,
  Search,
  Filter,
  MoreVertical,
  CheckCircle,
  XCircle,
  Ban,
  Trash2,
  Mail,
  Phone,
  Download,
  Calendar,
  AlertCircle,
  Eye
} from 'lucide-react';

export default function AdminUsersPage() {
  const { isArabic } = useOutletContext();
  const [activeTab, setActiveTab] = useState('students');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [teacherSubmissions, setTeacherSubmissions] = useState({}); // Map of teacher_id -> submission data
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedTeacherReview, setSelectedTeacherReview] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Download teacher profile as PDF
  const downloadTeacherPDF = async () => {
    if (!selectedTeacherReview) return;

    setDownloadingPDF(true);
    try {
      // Load html2pdf library dynamically if not already loaded
      if (!window.html2pdf) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const element = document.querySelector('.review-modal-body');
      if (!element) {
        throw new Error('Profile content not found');
      }

      const teacherName = selectedTeacherReview.full_name || 'Teacher';
      const filename = `${teacherName.replace(/\s+/g, '_')}_Profile.pdf`;

      const opt = {
        margin: 10,
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      await window.html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert(isArabic ? 'فشل تنزيل ملف PDF' : 'Failed to download PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Load users when tab changes
  useEffect(() => {
    loadUsers();
  }, [activeTab]);

  // Load users based on active tab
  async function loadUsers() {
    try {
      setLoading(true);
      setError(null);

      let data;
      if (activeTab === 'students') {
        data = await getAllStudents({ search: searchQuery, limit: 100 });
        setUsers(data.students || []);
      } else if (activeTab === 'teachers') {
        data = await getAllTeachers({ search: searchQuery, limit: 100 });
        const teachers = data.teachers || [];

        // Fetch pending submissions for teachers
        if (teachers.length > 0) {
          const teacherIds = teachers.map(t => t.id).filter(Boolean);
          const { data: submissionsData } = await supabase
            .from('teacher_onboarding_submissions')
            .select('teacher_id, submission_data, status, submitted_at')
            .in('teacher_id', teacherIds)
            .eq('status', 'pending');

          // Create a map of teacher_id -> submission
          const submissionsMap = {};
          if (submissionsData) {
            submissionsData.forEach(sub => {
              submissionsMap[sub.teacher_id] = sub;
            });
          }
          setTeacherSubmissions(submissionsMap);

          // Fetch ratings for all teachers
          const teacherEmails = teachers.map(t => t.email).filter(Boolean);
          const { data: ratingsData } = await getMultipleTeacherRatings(teacherEmails);

          // Merge ratings and submission status into teacher data
          const teachersWithRatings = teachers.map(teacher => ({
            ...teacher,
            rating: ratingsData?.[teacher.email]?.avg_rating || 0,
            ratings_count: ratingsData?.[teacher.email]?.ratings_count || 0,
            hasPendingSubmission: !!submissionsMap[teacher.id]
          }));

          setUsers(teachersWithRatings);
        } else {
          setUsers(teachers);
        }
      } else if (activeTab === 'institutes') {
        data = await getAllInstitutes({ search: searchQuery, limit: 100 });
        setUsers(data.institutes || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  // Handle search
  function handleSearch() {
    loadUsers();
  }

  // Handle approve/reject institute
  async function handleApproveInstitute(instituteId, approved) {
    const reason = prompt(
      isArabic
        ? approved ? 'سبب الموافقة (اختياري):' : 'سبب الرفض:'
        : approved ? 'Reason for approval (optional):' : 'Reason for rejection:'
    );
    
    if (!approved && !reason) {
      alert(isArabic ? 'يجب إدخال سبب الرفض' : 'Reason is required for rejection');
      return;
    }

    try {
      setActionInProgress(true);
      await approveInstitute(instituteId, approved, reason || '');
      alert(
        isArabic
          ? approved ? 'تمت الموافقة على المعهد بنجاح' : 'تم رفض المعهد'
          : approved ? 'Institute approved successfully' : 'Institute rejected'
      );
      loadUsers();
    } catch (err) {
      alert(err.message || 'Action failed');
    } finally {
      setActionInProgress(false);
      setShowActionMenu(null);
    }
  }

  // Handle approve/reject teacher
  async function handleApproveTeacher(teacherId, approved) {
    const reason = prompt(
      isArabic
        ? (approved ? 'سبب الموافقة (اختياري):' : 'سبب الرفض:')
        : (approved ? 'Reason for approval (optional):' : 'Reason for rejection:')
    );

    if (!approved && !reason) {
      alert(isArabic ? 'يجب إدخال سبب الرفض' : 'Rejection reason is required');
      return;
    }

    try {
      setActionInProgress(true);
      await approveTeacher(teacherId, approved, reason);
      
      // Update submission status in teacher_onboarding_submissions
      const { error: updateError } = await supabase
        .from('teacher_onboarding_submissions')
        .update({
          status: approved ? 'approved' : 'rejected',
          reviewed_at: new Date().toISOString(),
          review_reason: reason || null
        })
        .eq('teacher_id', teacherId)
        .eq('status', 'pending');

      if (updateError) {
        console.error('Error updating submission status:', updateError);
      }
      
      alert(
        isArabic
          ? (approved ? 'تمت الموافقة على المعلم بنجاح' : 'تم رفض المعلم')
          : (approved ? 'Teacher approved successfully' : 'Teacher rejected')
      );
      loadUsers();
    } catch (err) {
      alert(err.message || 'Action failed');
    } finally {
      setActionInProgress(false);
      setShowActionMenu(null);
    }
  }

  // Handle suspend/activate user
  async function handleUpdateStatus(userId, currentStatus) {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    const reason = prompt(
      isArabic
        ? `سبب ${newStatus === 'suspended' ? 'التعليق' : 'التفعيل'}:`
        : `Reason for ${newStatus === 'suspended' ? 'suspension' : 'activation'}:`
    );

    if (!reason) {
      alert(isArabic ? 'يجب إدخال السبب' : 'Reason is required');
      return;
    }

    try {
      setActionInProgress(true);
      await updateUserStatus(userId, activeTab.slice(0, -1), newStatus, reason);
      alert(
        isArabic
          ? `تم ${newStatus === 'suspended' ? 'تعليق' : 'تفعيل'} الحساب بنجاح`
          : `Account ${newStatus === 'suspended' ? 'suspended' : 'activated'} successfully`
      );
      loadUsers();
    } catch (err) {
      alert(err.message || 'Action failed');
    } finally {
      setActionInProgress(false);
      setShowActionMenu(null);
    }
  }

  // Handle delete user
  async function handleDeleteUser(userId) {
    const confirmed = window.confirm(
      isArabic
        ? 'هل أنت متأكد من حذف هذا المستخدم؟ هذا الإجراء لا يمكن التراجع عنه.'
        : 'Are you sure you want to delete this user? This action cannot be undone.'
    );

    if (!confirmed) return;

    const reason = prompt(isArabic ? 'سبب الحذف:' : 'Reason for deletion:');
    if (!reason) {
      alert(isArabic ? 'يجب إدخال السبب' : 'Reason is required');
      return;
    }

    try {
      setActionInProgress(true);
      await deleteUser(userId, activeTab.slice(0, -1), reason);
      alert(isArabic ? 'تم حذف المستخدم بنجاح' : 'User deleted successfully');
      loadUsers();
    } catch (err) {
      alert(err.message || 'Action failed');
    } finally {
      setActionInProgress(false);
      setShowActionMenu(null);
    }
  }

  // Handle row click to view user details
  function handleRowClick(user) {
    setSelectedUser(user);
    setShowUserDetails(true);
  }

  // Close user details modal
  function closeUserDetails() {
    setShowUserDetails(false);
    setSelectedUser(null);
  }

  // Tabs configuration
  const tabs = [
    { id: 'students', label: isArabic ? 'الطلاب' : 'Students', icon: Users, color: '#FF7426' },
    { id: 'teachers', label: isArabic ? 'المعلمين' : 'Teachers', icon: GraduationCap, color: '#4CAF50' },
    { id: 'institutes', label: isArabic ? 'المعاهد' : 'Institutes', icon: Building2, color: '#2196F3' }
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', minHeight: '100vh', paddingBottom: 300 }}>
      {/* Page Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{
          fontSize: 32,
          fontWeight: 700,
          color: '#232B3E',
          margin: '0 0 8px 0'
        }}>
          {isArabic ? 'إدارة المستخدمين' : 'User Management'}
        </h1>
        <p style={{ color: '#666', margin: 0, fontSize: 16 }}>
          {isArabic
            ? 'إدارة الطلاب والمعلمين والمعاهد'
            : 'Manage students, teachers, and institutes'}
        </p>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 24,
        borderBottom: '2px solid #e0e0e0'
      }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSearchQuery('');
              }}
              style={{
                padding: '12px 24px',
                background: activeTab === tab.id ? '#fff' : 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? `3px solid ${tab.color}` : '3px solid transparent',
                color: activeTab === tab.id ? tab.color : '#666',
                fontWeight: activeTab === tab.id ? 600 : 400,
                fontSize: 16,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.2s',
                marginBottom: -2
              }}
            >
              <Icon size={20} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Search and Filter Bar */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: 20,
        marginBottom: 24,
        display: 'flex',
        gap: 12,
        alignItems: 'center'
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search
            size={20}
            style={{
              position: 'absolute',
              left: isArabic ? 'auto' : 12,
              right: isArabic ? 12 : 'auto',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#999'
            }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder={isArabic ? 'البحث بالاسم أو البريد الإلكتروني...' : 'Search by name or email...'}
            style={{
              width: '100%',
              padding: '12px 12px 12px 40px',
              paddingLeft: isArabic ? 12 : 40,
              paddingRight: isArabic ? 40 : 12,
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              fontSize: 14,
              outline: 'none'
            }}
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={loading}
          style={{
            padding: '12px 24px',
            background: '#FF7426',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <Search size={16} />
          {isArabic ? 'بحث' : 'Search'}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <AlertCircle size={20} color="#c00" />
          <p style={{ color: '#c00', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Users List */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'visible',
        position: 'relative'
      }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{
              width: 48,
              height: 48,
              border: '4px solid #e0e0e0',
              borderTop: '4px solid #FF7426',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <p style={{ color: '#666' }}>
              {isArabic ? 'جاري التحميل...' : 'Loading...'}
            </p>
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <Users size={48} color="#ccc" style={{ margin: '0 auto 16px' }} />
            <p style={{ color: '#666', fontSize: 16 }}>
              {isArabic ? 'لا توجد نتائج' : 'No users found'}
            </p>
          </div>
        ) : (
          <div style={{ overflow: 'visible' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', position: 'relative' }}>
              <thead style={{ overflow: 'visible' }}>
                <tr style={{ background: '#f7f9fb', borderBottom: '2px solid #e0e0e0', overflow: 'visible' }}>
                  <th style={tableHeaderStyle}>{isArabic ? 'الاسم' : 'Name'}</th>
                  <th style={tableHeaderStyle}>{isArabic ? 'البريد الإلكتروني' : 'Email'}</th>
                  {activeTab === 'teachers' && (
                    <>
                      <th style={tableHeaderStyle}>{isArabic ? 'الحالة' : 'Status'}</th>
                      <th style={tableHeaderStyle}>{isArabic ? 'التقييم' : 'Rating'}</th>
                      <th style={tableHeaderStyle}>{isArabic ? 'شكل الدرس' : 'Lesson Format'}</th>
                    </>
                  )}
                  {activeTab === 'institutes' && (
                    <th style={tableHeaderStyle}>{isArabic ? 'الحالة' : 'Status'}</th>
                  )}
                  <th style={tableHeaderStyle}>{isArabic ? 'تاريخ التسجيل' : 'Joined'}</th>
                  <th style={tableHeaderStyle}>{isArabic ? 'الإجراءات' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody style={{ overflow: 'visible' }}>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => handleRowClick(user)}
                    style={{
                      borderBottom: '1px solid #f0f0f0',
                      overflow: 'visible',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f7f9fb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: 600, color: '#232B3E' }}>
                        {user.full_name || user.name}
                      </div>
                      {user.phone && (
                        <div style={{ fontSize: 12, color: '#999', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Phone size={12} />
                          {user.phone}
                        </div>
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Mail size={14} color="#999" />
                        {user.email}
                      </div>
                    </td>
                    {activeTab === 'teachers' && (
                      <>
                        <td style={tableCellStyle}>
                          {user.confirmation_completed ? (
                            <span style={{
                              padding: '4px 12px',
                              background: '#e8f5e9',
                              color: '#4CAF50',
                              borderRadius: 12,
                              fontSize: 12,
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4
                            }}>
                              <CheckCircle size={14} />
                              {isArabic ? 'موثق' : 'Verified'}
                            </span>
                          ) : user.hasPendingSubmission || user.form_submitted_at || (user.hear_about_taealam || user.why_join_taealam || user.teaching_motivation || user.teaching_experience) ? (
                            <span style={{
                              padding: '4px 12px',
                              background: '#fff3e0',
                              color: '#FF9800',
                              borderRadius: 12,
                              fontSize: 12,
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4
                            }}>
                              <AlertCircle size={14} />
                              {isArabic ? 'قيد المراجعة' : 'Pending Review'}
                            </span>
                          ) : (
                            <span style={{
                              padding: '4px 12px',
                              background: '#f5f5f5',
                              color: '#999',
                              borderRadius: 12,
                              fontSize: 12,
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4
                            }}>
                              <AlertCircle size={14} />
                              {isArabic ? 'ملف شخصي غير مكتمل' : 'Incomplete Profile'}
                            </span>
                          )}
                        </td>
                        <td style={tableCellStyle}>
                          <span style={{
                            padding: '4px 12px',
                            background: '#e3f2fd',
                            color: '#2196F3',
                            borderRadius: 12,
                            fontSize: 14,
                            fontWeight: 600
                          }}>
                            {user.rating ? Number(user.rating).toFixed(1) : 'N/A'}
                          </span>
                        </td>
                        <td style={tableCellStyle}>
                          {user.lesson_format_preference ? (
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: 12,
                              fontSize: 12,
                              fontWeight: 600,
                              background: user.lesson_format_preference === 'online_only' ? '#eff6ff'
                                : user.lesson_format_preference === 'face_to_face_only' ? '#fef3c7'
                                : '#f0fdf4',
                              color: user.lesson_format_preference === 'online_only' ? '#1e40af'
                                : user.lesson_format_preference === 'face_to_face_only' ? '#92400e'
                                : '#15803d'
                            }}>
                              {user.lesson_format_preference === 'online_only'
                                ? (isArabic ? '📱 عبر الإنترنت فقط' : '📱 Online Only')
                                : user.lesson_format_preference === 'face_to_face_only'
                                ? (isArabic ? '🏫 وجهاً لوجه فقط' : '🏫 Face-to-Face Only')
                                : (isArabic ? '🔄 كلاهما' : '🔄 Both')}
                            </span>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: 12, fontStyle: 'italic' }}>
                              {isArabic ? 'غير محدد' : 'Not set'}
                            </span>
                          )}
                        </td>
                      </>
                    )}
                    {activeTab === 'institutes' && (
                      <td style={tableCellStyle}>
                        {user.status === 'approved' || (user.is_active && !user.status) ? (
                          <span style={{
                            padding: '4px 12px',
                            background: '#e8f5e9',
                            color: '#4CAF50',
                            borderRadius: 12,
                            fontSize: 12,
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}>
                            <CheckCircle size={14} />
                            {isArabic ? 'موافق عليه' : 'Approved'}
                          </span>
                        ) : user.status === 'pending' || (!user.status && !user.is_active) ? (
                          <span style={{
                            padding: '4px 12px',
                            background: '#fff3e0',
                            color: '#FF9800',
                            borderRadius: 12,
                            fontSize: 12,
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}>
                            <AlertCircle size={14} />
                            {isArabic ? 'قيد المراجعة' : 'Pending Review'}
                          </span>
                        ) : user.status === 'rejected' ? (
                          <span style={{
                            padding: '4px 12px',
                            background: '#ffebee',
                            color: '#f44336',
                            borderRadius: 12,
                            fontSize: 12,
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}>
                            <XCircle size={14} />
                            {isArabic ? 'مرفوض' : 'Rejected'}
                          </span>
                        ) : null}
                      </td>
                    )}
                    <td style={tableCellStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#666', fontSize: 14 }}>
                        <Calendar size={14} />
                        {user.created_at
                          ? new Date(user.created_at).toLocaleDateString()
                          : (isArabic ? 'غير متوفر' : 'N/A')}
                      </div>
                    </td>
                    <td style={{...tableCellStyle, overflow: 'visible'}} onClick={(e) => e.stopPropagation()}>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {activeTab === 'teachers' && user.hasPendingSubmission && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const submission = teacherSubmissions[user.id];
                              if (submission) {
                                setSelectedTeacherReview(submission.submission_data);
                                setShowReviewModal(true);
                              }
                            }}
                            style={{
                              padding: '6px 12px',
                              background: '#037EF3',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#0268c7';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#037EF3';
                            }}
                          >
                            <Eye size={14} />
                            {isArabic ? 'عرض المراجعة' : 'View Review'}
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowActionMenu(showActionMenu === user.id ? null : user.id);
                          }}
                          disabled={actionInProgress}
                          style={{
                            padding: 8,
                            background: 'transparent',
                            border: '1px solid #e0e0e0',
                            borderRadius: 6,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <MoreVertical size={16} />
                        </button>

                        {/* Action Menu */}
                        {showActionMenu === user.id && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              position: 'absolute',
                              top: '100%',
                              right: isArabic ? 'auto' : 0,
                              left: isArabic ? 0 : 'auto',
                              marginTop: 4,
                              background: '#fff',
                              border: '1px solid #e0e0e0',
                              borderRadius: 8,
                              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                              zIndex: 9999,
                              minWidth: 180,
                              pointerEvents: 'auto',
                              overflow: 'visible'
                            }}>
                            {activeTab === 'teachers' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedTeacherReview(user);
                                  setShowReviewModal(true);
                                  setShowActionMenu(null);
                                }}
                                className="action-menu-item"
                              >
                                <Eye size={16} color="#037EF3" />
                                {isArabic ? 'عرض الملف الشخصي' : 'View Profile'}
                              </button>
                            )}
                            {activeTab === 'teachers' && (user.hasPendingSubmission || (!user.confirmation_completed && (user.form_submitted_at || user.hear_about_taealam || user.why_join_taealam || user.teaching_motivation || user.teaching_experience))) && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApproveTeacher(user.id, true);
                                  }}
                                  className="action-menu-item"
                                >
                                  <CheckCircle size={16} color="#4CAF50" />
                                  {isArabic ? 'الموافقة' : 'Approve'}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApproveTeacher(user.id, false);
                                  }}
                                  className="action-menu-item"
                                >
                                  <XCircle size={16} color="#f44336" />
                                  {isArabic ? 'الرفض' : 'Reject'}
                                </button>
                              </>
                            )}
                            {activeTab === 'institutes' && (user.status === 'pending' || (!user.status && !user.is_active)) && (
                              <>
                                <button
                                  onClick={() => handleApproveInstitute(user.id, true)}
                                  className="action-menu-item"
                                >
                                  <CheckCircle size={16} color="#4CAF50" />
                                  {isArabic ? 'الموافقة' : 'Approve'}
                                </button>
                                <button
                                  onClick={() => handleApproveInstitute(user.id, false)}
                                  className="action-menu-item"
                                >
                                  <XCircle size={16} color="#f44336" />
                                  {isArabic ? 'الرفض' : 'Reject'}
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleUpdateStatus(user.id, user.is_suspended ? 'suspended' : 'active')}
                              className="action-menu-item"
                            >
                              <Ban size={16} color="#FF9800" />
                              {user.is_suspended
                                ? (isArabic ? 'تفعيل' : 'Activate')
                                : (isArabic ? 'تعليق' : 'Suspend')}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="action-menu-item delete-item"
                            >
                              <Trash2 size={16} color="#f44336" />
                              {isArabic ? 'حذف' : 'Delete'}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      {!loading && users.length > 0 && (
        <div style={{
          marginTop: 24,
          padding: 16,
          background: '#f7f9fb',
          borderRadius: 8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ color: '#666', fontSize: 14 }}>
            {isArabic ? `إجمالي ${activeTab === 'students' ? 'الطلاب' : activeTab === 'teachers' ? 'المعلمين' : 'المعاهد'}: ${users.length}` : `Total ${activeTab}: ${users.length}`}
          </span>
          {activeTab === 'teachers' && (
            <div style={{ display: 'flex', gap: 24 }}>
              <span style={{ color: '#666', fontSize: 14 }}>
                {isArabic
                  ? `قيد المراجعة: ${users.filter(u => !u.confirmation_completed && (u.form_submitted_at || u.hear_about_taealam || u.why_join_taealam || u.teaching_motivation || u.teaching_experience)).length}`
                  : `Pending Review: ${users.filter(u => !u.confirmation_completed && (u.form_submitted_at || u.hear_about_taealam || u.why_join_taealam || u.teaching_motivation || u.teaching_experience)).length}`}
              </span>
              <span style={{ color: '#999', fontSize: 14 }}>
                {isArabic
                  ? `ملف غير مكتمل: ${users.filter(u => !u.confirmation_completed && !u.form_submitted_at && !u.hear_about_taealam && !u.why_join_taealam && !u.teaching_motivation && !u.teaching_experience).length}`
                  : `Incomplete: ${users.filter(u => !u.confirmation_completed && !u.form_submitted_at && !u.hear_about_taealam && !u.why_join_taealam && !u.teaching_motivation && !u.teaching_experience).length}`}
              </span>
            </div>
          )}
        </div>
      )}

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div
          onClick={closeUserDetails}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 20
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              maxWidth: 800,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '24px 32px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: activeTab === 'students' ? '#FFF7ED' : activeTab === 'teachers' ? '#E8F5E9' : '#E3F2FD'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#232B3E' }}>
                  {selectedUser.full_name || selectedUser.name}
                </h2>
                <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: 14 }}>
                  {activeTab === 'students' ? (isArabic ? 'طالب' : 'Student') :
                   activeTab === 'teachers' ? (isArabic ? 'معلم' : 'Teacher') :
                   (isArabic ? 'معهد' : 'Institute')}
                </p>
              </div>
              <button
                onClick={closeUserDetails}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: 28,
                  color: '#666',
                  cursor: 'pointer',
                  padding: 0,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: 32 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 24 }}>
                {/* Contact Information */}
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: '#232B3E', marginBottom: 12 }}>
                    {isArabic ? 'معلومات الاتصال' : 'Contact Information'}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 4 }}>
                        {isArabic ? 'البريد الإلكتروني' : 'Email'}
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Mail size={16} color="#666" />
                        <span style={{ fontSize: 14, color: '#232B3E' }}>{selectedUser.email}</span>
                      </div>
                    </div>
                    {selectedUser.phone && (
                      <div>
                        <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 4 }}>
                          {isArabic ? 'الهاتف' : 'Phone'}
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Phone size={16} color="#666" />
                          <span style={{ fontSize: 14, color: '#232B3E' }}>{selectedUser.phone}</span>
                        </div>
                      </div>
                    )}
                    {selectedUser.gender && (
                      <div>
                        <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 4 }}>
                          {isArabic ? 'الجنس' : 'Gender'}
                        </label>
                        <span style={{ fontSize: 14, color: '#232B3E' }}>{selectedUser.gender}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Account Information */}
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: '#232B3E', marginBottom: 12 }}>
                    {isArabic ? 'معلومات الحساب' : 'Account Information'}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 4 }}>
                        {isArabic ? 'تاريخ التسجيل' : 'Joined Date'}
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Calendar size={16} color="#666" />
                        <span style={{ fontSize: 14, color: '#232B3E' }}>
                          {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                    {activeTab === 'teachers' && (
                      <>
                        <div>
                          <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 4 }}>
                            {isArabic ? 'الحالة' : 'Status'}
                          </label>
                          {selectedUser.confirmation_completed ? (
                            <span style={{
                              padding: '4px 12px',
                              background: '#e8f5e9',
                              color: '#4CAF50',
                              borderRadius: 12,
                              fontSize: 12,
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4
                            }}>
                              <CheckCircle size={14} />
                              {isArabic ? 'موثق' : 'Verified'}
                            </span>
                          ) : selectedUser.form_submitted_at || (selectedUser.hear_about_taealam || selectedUser.why_join_taealam || selectedUser.teaching_motivation || selectedUser.teaching_experience) ? (
                            <span style={{
                              padding: '4px 12px',
                              background: '#fff3e0',
                              color: '#FF9800',
                              borderRadius: 12,
                              fontSize: 12,
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4
                            }}>
                              <AlertCircle size={14} />
                              {isArabic ? 'قيد المراجعة' : 'Pending Review'}
                            </span>
                          ) : (
                            <span style={{
                              padding: '4px 12px',
                              background: '#f5f5f5',
                              color: '#999',
                              borderRadius: 12,
                              fontSize: 12,
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4
                            }}>
                              <AlertCircle size={14} />
                              {isArabic ? 'ملف شخصي غير مكتمل' : 'Incomplete Profile'}
                            </span>
                          )}
                        </div>
                        {selectedUser.rating && (
                          <div>
                            <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 4 }}>
                              {isArabic ? 'التقييم' : 'Rating'}
                            </label>
                            <span style={{ fontSize: 18, fontWeight: 700, color: '#2196F3' }}>
                              {Number(selectedUser.rating).toFixed(1)} ⭐
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Teacher-specific Information */}
              {activeTab === 'teachers' && (
                <>
                  {/* Teaching Details */}
                  {(selectedUser.subjects || selectedUser.grade_levels || selectedUser.hourly_rate) && (
                    <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #e0e0e0' }}>
                      <h3 style={{ fontSize: 16, fontWeight: 600, color: '#232B3E', marginBottom: 16 }}>
                        {isArabic ? 'تفاصيل التدريس' : 'Teaching Details'}
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
                        {selectedUser.subjects && (
                          <div>
                            <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 8 }}>
                              {isArabic ? 'المواد' : 'Subjects'}
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {Array.isArray(selectedUser.subjects) ? selectedUser.subjects.map((subject, idx) => (
                                <span key={idx} style={{
                                  padding: '6px 12px',
                                  background: '#e3f2fd',
                                  color: '#2196F3',
                                  borderRadius: 16,
                                  fontSize: 12,
                                  fontWeight: 500
                                }}>
                                  {subject}
                                </span>
                              )) : <span style={{ fontSize: 14, color: '#666' }}>N/A</span>}
                            </div>
                          </div>
                        )}
                        {selectedUser.grade_levels && (
                          <div>
                            <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 8 }}>
                              {isArabic ? 'المستويات' : 'Grade Levels'}
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {Array.isArray(selectedUser.grade_levels) ? selectedUser.grade_levels.map((grade, idx) => (
                                <span key={idx} style={{
                                  padding: '6px 12px',
                                  background: '#f3e5f5',
                                  color: '#9C27B0',
                                  borderRadius: 16,
                                  fontSize: 12,
                                  fontWeight: 500
                                }}>
                                  {grade}
                                </span>
                              )) : <span style={{ fontSize: 14, color: '#666' }}>N/A</span>}
                            </div>
                          </div>
                        )}
                        {selectedUser.hourly_rate && (
                          <div>
                            <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 8 }}>
                              {isArabic ? 'السعر بالساعة' : 'Hourly Rate'}
                            </label>
                            <span style={{ fontSize: 20, fontWeight: 700, color: '#4CAF50' }}>
                              {selectedUser.hourly_rate} BHD
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Bio */}
                  {selectedUser.bio && (
                    <div style={{ marginTop: 24 }}>
                      <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 8 }}>
                        {isArabic ? 'النبذة' : 'Bio'}
                      </label>
                      <p style={{ fontSize: 14, color: '#232B3E', lineHeight: 1.6, margin: 0 }}>
                        {selectedUser.bio}
                      </p>
                    </div>
                  )}

                  {/* Confirmation Questions */}
                  {(selectedUser.hear_about_taealam || selectedUser.why_join_taealam || selectedUser.teaching_motivation || selectedUser.teaching_experience || selectedUser.cv_url || selectedUser.promotional_content_interest !== null) && (
                    <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #e0e0e0' }}>
                      <h3 style={{ fontSize: 16, fontWeight: 600, color: '#232B3E', marginBottom: 16 }}>
                        {isArabic ? 'استبيان التسجيل' : 'Registration Survey'}
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {selectedUser.hear_about_taealam && (
                          <div>
                            <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 8 }}>
                              {isArabic ? 'كيف سمعت عن تعلم؟' : 'How did you hear about Taealam?'}
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {Array.isArray(selectedUser.hear_about_taealam) ? selectedUser.hear_about_taealam.map((item, idx) => (
                                <span key={idx} style={{
                                  padding: '6px 12px',
                                  background: '#fff3e0',
                                  color: '#F57C00',
                                  borderRadius: 16,
                                  fontSize: 12
                                }}>
                                  {item}
                                </span>
                              )) : <span style={{ fontSize: 14, color: '#666' }}>{selectedUser.hear_about_taealam}</span>}
                            </div>
                          </div>
                        )}
                        {selectedUser.why_join_taealam && (
                          <div>
                            <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 8 }}>
                              {isArabic ? 'لماذا اخترت الانضمام إلى تعلم؟' : 'Why did you choose to join Taealam?'}
                            </label>
                            <p style={{ fontSize: 14, color: '#232B3E', margin: 0, lineHeight: 1.6 }}>
                              {selectedUser.why_join_taealam}
                            </p>
                          </div>
                        )}
                        {selectedUser.teaching_motivation && (
                          <div>
                            <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 8 }}>
                              {isArabic ? 'ما الذي يحفزك على الاستمرار في التدريس؟' : 'What motivates you to continue teaching?'}
                            </label>
                            <p style={{ fontSize: 14, color: '#232B3E', margin: 0, lineHeight: 1.6 }}>
                              {selectedUser.teaching_motivation}
                            </p>
                          </div>
                        )}
                        {selectedUser.teaching_experience && (
                          <div>
                            <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 8 }}>
                              {isArabic ? 'الخبرة التدريسية' : 'Teaching Experience'}
                            </label>
                            <p style={{ fontSize: 14, color: '#232B3E', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                              {selectedUser.teaching_experience}
                            </p>
                          </div>
                        )}
                        {selectedUser.promotional_content_interest !== null && selectedUser.promotional_content_interest !== undefined && (
                          <div>
                            <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 8 }}>
                              {isArabic ? 'هل تهتم بالمشاركة في محتوى ترويجي؟' : 'Interested in participating in promotional content?'}
                            </label>
                            <span style={{
                              padding: '6px 12px',
                              background: selectedUser.promotional_content_interest ? '#e8f5e9' : '#ffebee',
                              color: selectedUser.promotional_content_interest ? '#2e7d32' : '#c62828',
                              borderRadius: 16,
                              fontSize: 14,
                              fontWeight: 500,
                              display: 'inline-block'
                            }}>
                              {selectedUser.promotional_content_interest
                                ? (isArabic ? 'نعم' : 'Yes')
                                : (isArabic ? 'لا' : 'No')}
                            </span>
                          </div>
                        )}
                        {selectedUser.cv_url && (
                          <div>
                            <label style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 8 }}>
                              {isArabic ? 'السيرة الذاتية' : 'CV/Resume'}
                            </label>
                            <a
                              href={selectedUser.cv_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '8px 16px',
                                background: '#e3f2fd',
                                color: '#2196F3',
                                borderRadius: 8,
                                textDecoration: 'none',
                                fontSize: 14,
                                fontWeight: 500,
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#2196F3';
                                e.currentTarget.style.color = '#fff';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#e3f2fd';
                                e.currentTarget.style.color = '#2196F3';
                              }}
                            >
                              📄 {isArabic ? 'عرض السيرة الذاتية' : 'View CV'}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Rating Distribution & Recent Reviews */}
                  <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #e0e0e0' }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, color: '#232B3E', marginBottom: 16 }}>
                      {isArabic ? 'تقييمات الطلاب' : 'Student Ratings'}
                      {selectedUser.rating > 0 && (
                        <span style={{ marginLeft: 12, fontSize: 20, fontWeight: 700, color: '#2196F3' }}>
                          {Number(selectedUser.rating).toFixed(1)} ⭐
                        </span>
                      )}
                    </h3>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                      gap: 24
                    }}>
                      {/* Rating Breakdown */}
                      <div>
                        <p style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
                          Debug: Email = {selectedUser.email}, Rating = {selectedUser.rating}, Count = {selectedUser.ratings_count}
                        </p>
                        <RatingBreakdown
                          teacherEmail={selectedUser.email}
                          isArabic={isArabic}
                        />
                      </div>

                      {/* Recent Reviews */}
                      <div>
                        <RecentReviews
                          teacherEmail={selectedUser.email}
                          isArabic={isArabic}
                          limit={5}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 32px',
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={closeUserDetails}
                style={{
                  padding: '10px 24px',
                  background: '#FF7426',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {isArabic ? 'إغلاق' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Critical fix: Ensure table elements don't clip the dropdown */
        table, thead, tbody, tr, td, th {
          overflow: visible !important;
        }

        /* Action menu button hover styles */
        .action-menu-item {
          width: 100%;
          padding: 12px 16px;
          background: transparent;
          border: none;
          text-align: left;
          cursor: pointer;
          fontSize: 14px;
          color: #232B3E;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s;
        }

        .action-menu-item:hover {
          background: #f7f9fb;
        }

        .action-menu-item:active {
          background: #e8f0fe;
        }

        .action-menu-item.delete-item {
          color: #f44336;
        }

        .action-menu-item.delete-item:hover {
          background: #fee;
        }

        /* Review Modal Styles */
        .review-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 20px;
        }

        .review-modal-content {
          background: white;
          border-radius: 12px;
          max-width: 1200px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
        }

        .review-modal-header {
          padding: 24px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          background: white;
          z-index: 10;
        }

        .review-modal-body {
          padding: 24px;
        }
      `}</style>

      {/* Review Modal */}
      {showReviewModal && selectedTeacherReview && (
        <div className="review-modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="review-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="review-modal-header">
              <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: 0 }}>
                {isArabic ? 'مراجعة ملف المعلم' : 'Teacher Profile Review'}
              </h2>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  onClick={downloadTeacherPDF}
                  disabled={downloadingPDF}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    background: downloadingPDF ? '#9ca3af' : '#FF7426',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: downloadingPDF ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: downloadingPDF ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!downloadingPDF) {
                      e.target.style.background = '#ff8c4d';
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!downloadingPDF) {
                      e.target.style.background = '#FF7426';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <Download size={16} />
                  {downloadingPDF
                    ? (isArabic ? 'جارٍ التنزيل...' : 'Downloading...')
                    : (isArabic ? 'تنزيل PDF' : 'Download PDF')}
                </button>
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setSelectedTeacherReview(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '8px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="review-modal-body">
              <Step10Review
                wizardData={selectedTeacherReview}
                onBack={() => {}}
                onSubmit={() => {}}
                isSubmitting={false}
                translations={wizardTranslations[isArabic ? 'AR' : 'EN'] || {}}
                isArabic={isArabic}
                language={isArabic ? 'AR' : 'EN'}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const tableHeaderStyle = {
  padding: '16px',
  textAlign: 'left',
  fontSize: 14,
  fontWeight: 600,
  color: '#666'
};

const tableCellStyle = {
  padding: '16px',
  fontSize: 14,
  color: '#232B3E'
};
